import { buildRpaDomainSeoBundle } from "../rpaSeoUtils";

export const INSURANCE_DOMAIN_INTRO = {
  title: "Insurance",
  content: "Document AI + RPA and workflow automation across Life Insurance, Broker Channel, General Insurance. Each use case includes challenge and solution detail, process diagrams, and human-in-the-loop review for enterprises in India, UAE, KSA, UK, and USA.",
  seoDescription: "Insurance automation use cases — document AI + RPA and workflow automation across Life Insurance, Broker Channel, General Insurance. Validation, approvals, system updates, and exception handling.",
};

const slugify = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function withMeta(uc) {
  return {
    ...uc,
    groupId: slugify(uc.subIndustry),
    seoTitle: uc.title,
    title: uc.shortTitle || uc.title,
    systems: uc.systems || SYSTEMS,
  };
}

const SYSTEMS = "Policy Admin System, Claims System, CRM, Document Management, Email, Power BI";

const RAW = [
  {
    id: "application-and-proposal-form-validation",
    subIndustry: "Life Insurance",
    department: "New Business",
    businessProcess: "Application and proposal form validation",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Application and proposal form validation",
    title: "Application And Proposal Form Validation Automation for Insurance",
    seoKeywords: "insurance, application and proposal form validation, AI automation, RPA, agentic AI, document AI",
    challenge: "Application forms and supporting documents are checked manually, increasing turnaround time and rework.",
    solution: "Implement Document AI + RPA to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "Document AI + RPA",
    buyerPersona: "New Business Head, CIO",
    systems: "Policy Admin, CRM, DMS, Email, Core Insurance Platform",
    agentsRequired: "Document Extraction Agent, Application Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and application and proposal form validation rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready application and proposal form validation record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "broker-onboarding-and-commission-reconciliation",
    subIndustry: "Broker Channel",
    department: "Distribution",
    businessProcess: "Broker onboarding and commission reconciliation",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Broker onboarding and commission reconciliation",
    title: "Broker Onboarding And Commission Reconciliation Automation for Insurance",
    seoKeywords: "insurance, broker onboarding and commission reconciliation, AI automation, RPA, agentic AI, document AI",
    challenge: "Broker documents, licensing details and commission statements are validated manually.",
    solution: "Implement Document AI + RPA to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "Document AI + RPA",
    buyerPersona: "Distribution Head, CFO",
    systems: "Broker Portal, CRM, ERP, Commission System",
    agentsRequired: "Document Extraction Agent, Broker Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and broker onboarding and commission reconciliation rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready broker onboarding and commission reconciliation record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "Medium",
  },
  {
    id: "nominee-and-policy-change-processing",
    subIndustry: "Life Insurance",
    department: "Policy Servicing",
    businessProcess: "Nominee and policy change processing",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Nominee and policy change processing",
    title: "Nominee And Policy Change Processing Automation for Insurance",
    seoKeywords: "insurance, nominee and policy change processing, AI automation, RPA, agentic AI, document AI",
    challenge: "Policy change requests are checked manually against forms, IDs and policy rules.",
    solution: "Implement Document AI + RPA to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "Document AI + RPA",
    buyerPersona: "Policy Servicing Head",
    systems: "Policy Admin, CRM, DMS, Email",
    agentsRequired: "Document Extraction Agent, Nominee Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and nominee and policy change processing rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready nominee and policy change processing record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "Medium",
  },
  {
    id: "policy-endorsement-processing",
    subIndustry: "General Insurance",
    department: "Policy Servicing",
    businessProcess: "Policy endorsement processing",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Policy endorsement processing",
    title: "Policy Endorsement Processing Automation for Insurance",
    seoKeywords: "insurance, policy endorsement processing, AI automation, RPA, agentic AI, document AI",
    challenge: "Endorsement requests require manual reading, validation, premium impact calculation and system update.",
    solution: "Implement Document AI + RPA to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "Document AI + RPA",
    buyerPersona: "Operations Head",
    systems: "Policy Admin, CRM, Rating Engine, Email",
    agentsRequired: "Document Extraction Agent, Policy Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and policy endorsement processing rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready policy endorsement processing record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
];
export const INSURANCE_USE_CASES = RAW.map(withMeta);
const GROUP_ORDER = ["Life Insurance", "Broker Channel", "General Insurance"];
export const INSURANCE_GROUPS = GROUP_ORDER.map((label) => ({
  id: slugify(label),
  label,
})).filter((g) => INSURANCE_USE_CASES.some((uc) => uc.groupId === g.id));
const seo = buildRpaDomainSeoBundle({
  domainSlug: 'insurance-domain',
  domainIntro: INSURANCE_DOMAIN_INTRO,
  useCases: INSURANCE_USE_CASES,
});
export const INSURANCE_USE_CASES_SEO = seo.useCasesSeo;
export const INSURANCE_DOMAIN_FAQS = seo.domainFaqs;
export const INSURANCE_ITEM_LIST_JSONLD = seo.itemListJsonLd;
export const INSURANCE_FAQ_JSONLD = seo.faqJsonLd;
export const INSURANCE_BREADCRUMB_JSONLD = seo.breadcrumbJsonLd;
export const INSURANCE_WEBPAGE_JSONLD = seo.webPageJsonLd;

